import { io, type Socket } from 'socket.io-client';

import { env } from '@/shared/config/env';

/**
 * The server pushes this when something in the director's outstanding list
 * changes — an agent lodges a form, a co-director signs, a payment settles.
 */
export const OUTSTANDING_ITEMS_EVENT = 'OUTSTANDING_ITEMS_TABLE_EVENT';

/** The server's keepalive. Subscribed to and ignored: without a listener,
 *  socket.io logs an "unhandled event" warning on every ping. */
const HEALTH_CHECK_EVENT = 'HEALTH_CHECK_SOCKET';

let socket: Socket | null = null;

/** The director the live `socket` is authenticated as. `auth` is sent once, at
 *  handshake, so a different member cannot reuse the connection — see
 *  `connectSocket`. */
let connectedMemberId = '';

/**
 * A teardown waiting to run at the end of the current task.
 *
 * The shell's cleanup closes the socket on unmount, which is right on logout
 * and wrong in development: React StrictMode runs effect → cleanup → effect, so
 * the connection is aborted mid-handshake ("WebSocket is closed before the
 * connection is established") and immediately rebuilt. Reference counting does
 * not help — the count returns to zero between the two mounts, because the
 * consumers are sequential rather than concurrent.
 *
 * Deferring by a macrotask lets the remount reclaim the same socket: a real
 * unmount has nothing following it and the close goes through, while
 * StrictMode's remount cancels it before it fires.
 */
let pendingClose: ReturnType<typeof setTimeout> | null = null;

/** Tears the connection down immediately. The deferral lives in
 *  `disconnectSocket`; this is the work it defers, and what a session change
 *  runs straight away. */
const closeNow = (): void => {
  if (pendingClose !== null) {
    clearTimeout(pendingClose);
    pendingClose = null;
  }
  if (socket === null) return;

  socket.removeAllListeners();
  socket.disconnect();
  socket = null;
  connectedMemberId = '';
};

/**
 * The app's single socket connection.
 *
 * A module-level singleton rather than one per component: the legacy portal
 * connected from two places (`Login` and `AppMain`) guarded by a shared `_socket`
 * variable, and every mounted listener received every event. One connection,
 * opened when a session exists and closed when it ends.
 *
 * Returns null when no server is configured, which is the normal state in
 * development and in tests — the caller treats that as "no live updates" rather
 * than as an error, since tag invalidation already refreshes after every action.
 */
export const connectSocket = (memberId: string): Socket | null => {
  if (env.VITE_WEBSOCKET_SERVER_URL === '' || memberId === '') return null;

  // A remount arriving before the deferred close fires reclaims the live
  // connection, which is what keeps StrictMode from rebuilding it — but only
  // for the same director. `auth` is sent once at handshake, so reclaiming
  // another member's socket would leave the new director listening on the
  // previous one's channel and never on their own. A session change closes the
  // old connection now rather than a tick from now.
  if (socket !== null && connectedMemberId !== memberId) {
    closeNow();
  }

  if (pendingClose !== null) {
    clearTimeout(pendingClose);
    pendingClose = null;
  }

  if (socket !== null) return socket;

  connectedMemberId = memberId;

  socket = io(env.VITE_WEBSOCKET_SERVER_URL, {
    auth: { memberId, companyId: '' },
    // The server exposes no HTTP polling endpoint — its handshake answers
    // `{"code":0,"message":"Transport unknown"}` to a polling request. Since
    // socket.io tries polling FIRST by default, leaving this unset means every
    // connection fails on `xhr poll error` before websocket is ever attempted.
    transports: ['websocket'],
    // The legacy client let socket.io retry forever with no ceiling, so a
    // server outage produced a reconnect storm from every open tab.
    reconnectionAttempts: 5,
    reconnectionDelayMax: 10_000,
  });

  socket.on(HEALTH_CHECK_EVENT, () => undefined);

  return socket;
};

/**
 * Closes the connection, at the end of the current task rather than instantly.
 *
 * See `pendingClose`: the delay is what lets a StrictMode remount cancel the
 * teardown instead of aborting a handshake. On a real unmount nothing cancels
 * it and the socket closes a tick later, which no caller can observe.
 */
export const disconnectSocket = (): void => {
  if (socket === null) return;

  // Rescheduled rather than skipped when one is already pending: a teardown
  // that arrives while another is queued still means "this consumer is gone",
  // and returning early here dropped the close on the floor whenever cleanup
  // ran twice in one task — the socket then outlived the session.
  if (pendingClose !== null) clearTimeout(pendingClose);

  pendingClose = setTimeout(closeNow, 0);
};
